import React, { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { useAuthentication } from "../../../context/authentication";
import { DottedMenu } from "../../../context/dropdownMenu/DropdownMenuProvider";
import { useToast } from "../../../context/toast";
import { useWallets } from "../../../context/wallets";
import { Wallet } from "../../../utils/types";

interface Props {
  disabled?: boolean;
  wallet: Wallet;
}

const WalletActionsButton: React.FC<Props> = ({ disabled, wallet }) => {
  const { setModalWarningConfig } = useToast();
  const { wrapActionWithAuth } = useAuthentication();
  const {
    getActiveWalletVerification,
    removeWallet,
    syncHotspots,
    trackWallet
  } = useWallets();
  const history = useHistory();

  const walletVerificationActions = useMemo(() => {
    switch (wallet.current_verification_status) {
      case "completed":
        return [];
      case "unverified":
        return [
          {
            text: "Verify wallet",
            onClick: wrapActionWithAuth(() => {
              history.push(
                `/workspace/wallets/verify/${wallet.address}/confirm-wallet`
              );
            })
          }
        ];
      case "pending":
        return getActiveWalletVerification(wallet.address)
          ? [
              {
                text: "Finish verification",
                onClick: wrapActionWithAuth(() => {
                  history.push(
                    `/workspace/wallets/verify/${wallet.address}/donate`
                  );
                })
              }
            ]
          : [];
    }
  }, [
    getActiveWalletVerification,
    history,
    wallet.address,
    wallet.current_verification_status,
    wrapActionWithAuth
  ]);

  return (
    <DottedMenu
      text="Open wallet actions"
      disabled={disabled}
      menuItems={[
        ...walletVerificationActions,
        {
          text: "Sync hotspots",
          onClick: wrapActionWithAuth(() => {
            syncHotspots(wallet);
          })
        },
        {
          text: wallet.tracked ? "Stop tracking hotspots" : "Track hotspots",
          onClick: wrapActionWithAuth(() => {
            setModalWarningConfig({
              title: !wallet.tracked
                ? "Track wallet hotspots"
                : "Stop tracking wallet hotspots",
              confirmButtonText: "Confirm",
              cancelButtonClassname:
                "sm:w-auto text-gray-600 bg-transparent border-gray-600 dark:text-white text-sm",
              confirmButtonClassname:
                "w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-hotspotty-600 font-medium text-white hover:bg-hotspotty-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-hotspotty-500 md:ml-3 sm:w-auto text-sm",
              message: !wallet.tracked
                ? "This action will automatically add hotspots to your workspace when they are linked to this wallet on the blockchain in the future."
                : "This action will stop adding hotspots to your workspace when they are linked to this wallet on the blockchain in the future. The hotspots of this wallet that were automatically added to your workspace in the past will not be removed when completing this action. But it will allow you to manually remove individual hotspots of this wallet from now on.",
              open: true,
              resolve: async () => {
                await trackWallet(wallet.address, !wallet.tracked);
              }
            });
          })
        },
        {
          text: "Remove wallet",
          onClick: wrapActionWithAuth(() => {
            setModalWarningConfig({
              open: true,
              resolve: () => {
                removeWallet(wallet);
              }
            });
          })
        }
      ]}
    />
  );
};

export default WalletActionsButton;
